import type { ApiNumberDataType } from "@/lib/dynamicForms/constants/api-number-data-type.constant";
import type { FormEditorEntity } from "../form-editor-entity.types";
import { resolveBuilderSectionLabel } from "../lib/resolve-builder-section-label";
/**
 * Builder palette and persisted field kinds for the ticket type form editor (ADO #889).
 * Distinct from {@link FieldType} in dynamicFormLib — project with `builderStateToDynamicFormSchema`.
 */
export const PALETTE_FIELD_TYPES = [
	"text",
	"textarea",
	"number",
	"email",
	"phone",
	"date",
	"time",
	"dateTime",
	"select",
	"radio",
	"checkbox",
	"file",
	"url",
	"multiSelect",
	"users",
	"signature",
	"address",
] as const;

export type PaletteFieldType = (typeof PALETTE_FIELD_TYPES)[number];

export interface BuilderFieldData {
	id: string;
	name: string;
	label: string;
	fieldType: PaletteFieldType;
	/** When `fieldType` is `"number"`: API `FieldDefinition.dataType`. */
	numberApiDataType?: ApiNumberDataType;
	required: boolean;
	placeholder?: string;
	helpText?: string;
	options: { value: string; label: string }[];
	/** When `fieldType` is `"file"`: `accept` on the file input (e.g. `image/*,.pdf`). */
	fileAccept?: string;
	/** When `fieldType` is `"file"`: allow more than one file. @default true */
	fileMultiple?: boolean;
	/** When `fieldType` is `"file"`: max files when multiple is enabled. */
	fileMaxFiles?: number;
	/** When `fieldType` is `"file"`: drop zone vs button trigger. @default "dropZone" */
	filePresentation?: "dropZone" | "button";
	/** When `fieldType` is `"textarea"`: visible height in rows. @default 3 */
	textAreaRows?: number;
}

export interface BuilderSectionData {
	id: string;
	label: string;
	helpText?: string;
	collapsible: boolean;
	defaultExpanded: boolean;
}

export type BuilderNode =
	| {
			kind: "section";
			section: BuilderSectionData;
			childFieldIds: string[];
	  }
	| {
			kind: "field";
			field: BuilderFieldData;
			parentId: "root" | string;
	  };

export interface TicketTypeFormBuilderState {
	/** Maps to {@link DynamicFormSchema.title} when projecting the schema. */
	formTitle: string;
	/** Maps to {@link DynamicFormSchema.description} when projecting the schema. */
	formDescription: string;
	rootIds: string[];
	nodes: Record<string, BuilderNode>;
	selectedId: string | null;
}

export function createDefaultSection(
	id: string,
	label: string,
	formEditorEntity: FormEditorEntity,
): BuilderSectionData {
	return {
		id,
		label: resolveBuilderSectionLabel(label, formEditorEntity),
		helpText: "",
		collapsible: false,
		defaultExpanded: true,
	};
}
